import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PencilLine, RotateCcw } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CONTENT_QUERY } from "@/context/ContentContext";
import { CONTACT, MARQUEE_WORDS } from "@/data/site";

const inp = "w-full rounded-xl bg-cream/5 border border-cream/15 px-4 py-2.5 text-cream outline-none focus:border-terra-soft placeholder:text-cream/40";
const LIST_QUERY = ["admin-content"];
const FIELDS = [
  ["contact.phone", "Phone (10 digits)", CONTACT.phone],
  ["contact.email", "Email", CONTACT.email],
  ["contact.address", "Office address", CONTACT.address],
  ["marquee.words", "Scrolling words on home page (comma separated)", MARQUEE_WORDS.join(", ")],
];

function useContentList() {
  return useQuery({
    queryKey: LIST_QUERY,
    queryFn: () => api.get("/admin/content").then((r) => (Array.isArray(r.data) ? r.data : [])),
  });
}

function useSave() {
  const qc = useQueryClient();
  return async (fn, ok) => {
    try {
      await fn();
      await Promise.all([qc.invalidateQueries({ queryKey: LIST_QUERY }), qc.invalidateQueries({ queryKey: CONTENT_QUERY })]);
      toast.success(ok);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
}

function KeyField({ k, label, fallback, current }) {
  const save = useSave();
  const [value, setValue] = useState(current ?? fallback);
  useEffect(() => { setValue(current ?? fallback); }, [current, fallback]);
  const dirty = value.trim() && value.trim() !== (current ?? fallback);
  return (
    <label className="block">
      <span className="eyebrow text-cream/70 block mb-2">{label}</span>
      <div className="flex gap-2">
        <input value={value} onChange={(e) => setValue(e.target.value)} className={inp} data-testid={`content-field-${k}`} />
        <button disabled={!dirty} onClick={() => save(() => api.put("/admin/content", { key: k, value: value.trim() }), "Saved")}
          className="rounded-full bg-terra px-4 font-semibold text-paper disabled:opacity-40" data-testid={`content-field-save-${k}`}>Save</button>
      </div>
    </label>
  );
}

function ChangedList({ items }) {
  const save = useSave();
  const [q, setQ] = useState("");
  const shown = items.filter((i) => !q || `${i.key} ${i.value}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl border border-cream/10 bg-cream/5 p-5" data-testid="content-changed-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <p className="font-display text-xl font-bold">Text you have changed ({items.length})</p>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className={`${inp} sm:max-w-xs`} data-testid="content-search-input" />
      </div>
      {!shown.length && <p className="text-cream/60 text-sm">Nothing changed yet. Use the visual editor to change any text on the website.</p>}
      <ul className="divide-y divide-cream/10">
        {shown.map((i) => (
          <li key={i.key} className="py-3 flex items-start justify-between gap-4" data-testid={`content-item-${i.key}`}>
            <div className="min-w-0"><p className="font-mono text-xs text-cream/60 break-all">{i.key}</p><p className="mt-1 text-cream/90 break-words">{i.value}</p></div>
            <button onClick={() => save(() => api.delete("/admin/content", { params: { key: i.key } }), "Original text restored")} title="Restore original"
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-cream/20 px-3 py-1.5 text-xs hover:bg-cream hover:text-ink" data-testid={`content-reset-${i.key}`}>
              <RotateCcw className="h-3.5 w-3.5" /> Original
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ContentManager() {
  const { data } = useContentList();
  const items = data ?? [];
  const byKey = Object.fromEntries(items.map((i) => [i.key, i.value]));
  return (
    <div className="space-y-6" data-testid="content-manager">
      <div className="rounded-2xl bg-terra/15 border border-terra/30 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="font-display text-xl font-bold">Edit any text or photo on the website</p>
          <p className="text-cream/75 text-sm mt-1">Opens your website in editing mode. Click outlined text to type, or <b>Change photo</b> on any image. Changes save automatically.</p>
        </div>
        <Link to="/?edit=1" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-terra px-5 py-3 font-semibold text-paper hover:bg-terra-soft" data-testid="open-visual-editor-btn">
          <PencilLine className="h-4 w-4" /> Open visual editor
        </Link>
      </div>
      <div className="rounded-2xl border border-cream/10 bg-cream/5 p-5 grid gap-5 md:grid-cols-2" data-testid="contact-details-form">
        <p className="md:col-span-2 font-display text-xl font-bold">Contact details &amp; scrolling words</p>
        {FIELDS.map(([k, label, fallback]) => <KeyField key={k} k={k} label={label} fallback={fallback} current={byKey[k]} />)}
      </div>
      <ChangedList items={items} />
    </div>
  );
}
